<template>
  <div id="page-address-form">
    <vs-row>
      <vs-col>
        <card-page card-title="Search Address">
          <div class="m-4">
            <address-lookup @selectAddress="selectAddress" />
          </div>
        </card-page>
        <card-page :card-title="localTitle" :card-subtitle="localSubtitle">
          <vue-form-generator
            class="mt-4"
            ref="form"
            :model="model"
            :options="formOptions"
            :schema="schema"
          />
        </card-page>
        <card-page card-title="Phone Numbers">
          <additive-widget
            ref="formAddressPhoneNumbers"
            v-model="model"
            :form-options="formOptions"
            :schema="addressPhoneNumbersSchema"
            label="Phone Numbers"
            primary-toggle
            primary-field-name="IsPrimaryNumber"
            mandatory
          />
        </card-page>
      </vs-col>
    </vs-row>
  </div>
</template>

<script>
import AddressLookup from '../../../components/address/AddressLookup';
import CardPage from '../../../components/Views/CardPage';
import FormModelMixin from '../../../../components/mixins/FormModelMixin';
import AdditiveWidget from '../../../../components/form/fields/AdditiveWidget';
import { selectOptions, arrayOptions } from '@/components/form/FormElementOption';
import CountryList from 'countries-list';
import FormFieldValidationMixin from '../../../../components/mixins/FormFieldValidationMixin';

export default {
  components: {
    AddressLookup,
    CardPage,
    AdditiveWidget
  },
  props: {
    value: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      required: false,
      default: false
    },
    title: {
      type: String,
      default: null
    },
    subtitle: {
      type: String,
      default: null
    }
  },
  mixins: [FormModelMixin, FormFieldValidationMixin],
  data() {
    return {
      additiveForms: ['formAddressPhoneNumbers'],
      addresses: JSON.parse(JSON.stringify(this.value)),
      model: {
        AddressType_FK: null,
        AddressLine1: null,
        AddressLine2: null,
        AddressLine3: null,
        City: null,
        Province: null,
        Region: null,
        PostCode: null,
        Country: null,
        phone_numbers: [
          {
            PhoneType_FK: null,
            CountryCode: null,
            PhoneNumber: null,
            PhoneExtension: null,
          },
        ],
      },
      schema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                type: 'vueMultiSelect',
                styleClasses: 'w-full',
                label: 'Address Type',
                model: 'AddressType_FK',
                values: () => this.classOfBusiness,
                selectOptions,
                required: true,
                validator: 'required'
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-3/4',
                label: 'Address Line 1',
                model: 'AddressLine1',
                required: true,
                validator: 'required'
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/4',
                label: 'Number',
                model: 'AddressLine2'
              },
              // {
              //   type: 'input',
              //   inputType: 'text',
              //   styleClasses: 'w-1/3',
              //   label: 'Address Line 3',
              //   model: 'AddressLine3'
              // },

              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'City',
                model: 'City',
                required: true,
                validator: 'required'
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Province',
                model: 'Province',
                required: true,
                validator: 'required'
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Region',
                model: 'Region',
                required: true,
                validator: 'required'
              },
              {
                type: 'input',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Post Code',
                model: 'PostCode',
                required: true,
                validator: 'required'
              },
              {
                type: 'vueMultiSelect',
                inputType: 'text',
                styleClasses: 'w-1/3',
                label: 'Country',
                model: 'Country',
                values: () => this.countryOptions,
                selectOptions: {
                  showLabels: false,
                  hideSelected: false
                },
                required: true,
                validator: 'required'
              }
            ]
          }
        ]
      },
      addressPhoneNumbersSchema: {
        groups: [
          {
            styleClasses: 'w-full',
            fields: [
              {
                model: 'phone_numbers',
                ...arrayOptions,
                showRemoveButton: true,
                items: {
                  type: 'object',
                  styleClasses: 'w-full',
                  schema: {
                    groups: [
                      {
                        styleClasses: 'w-full',
                        fields: [
                          {
                            type: 'vueMultiSelect',
                            styleClasses: 'w-1/3',
                            label: 'Phone Type',
                            model: 'PhoneType_FK',
                            values: () => this.companyAddressPhoneNumbers,
                            selectOptions,
                            required: true,
                            validator: 'required'
                          },
                          {
                            type: 'vueMultiSelect',
                            styleClasses: 'w-1/3',
                            label: 'Country Code',
                            model: 'CountryCode',
                            values: () => this.countryCodeOptions,
                            selectOptions,
                            required: true,
                            validator: 'required'
                          },
                          {
                            type: 'label',
                            styleClasses: 'w-1/3',
                          },
                          {
                            type: 'input',
                            inputType: 'text',
                            styleClasses: 'w-1/3 pt-4',
                            label: 'Phone Number',
                            model: 'PhoneNumber',
                            required: true,
                            validator: [
                              'required',
                              'string',
                              model => {
                                const max = 25;

                                if (isNaN(model)) {
                                  return 'Value must be a number';
                                }

                                if (model && model.length > max) {
                                  return `Value must be lower than ${max}`;
                                }
                              }
                            ]
                          },
                          {
                            type: 'input',
                            inputType: 'text',
                            styleClasses: 'w-1/3 pt-4',
                            label: 'Extension',
                            model: 'PhoneExtension',
                            validator: 'string',
                          }
                        ]
                      }
                    ]
                  }
                }
              }
            ]
          }
        ]
      },
      formOptions: {
        validateAfterLoad: false,
        validateAfterChanged: true
      },
      classOfBusiness: [],
      companyAddressPhoneNumbers: [],
      options: [
        {
          endpoint: () => this.$api.companyAddress.getOptions(),
          optionsFieldName: 'classOfBusiness',
          nameFieldName: 'Type',
          idFieldName: 'AddressType_FK'
        },
        {
          endpoint: () => this.$api.companyAddressPhoneNumber.getOptions(),
          optionsFieldName: 'companyAddressPhoneNumbers',
          nameFieldName: 'Type',
          idFieldName: 'PhoneType_FK',
          modelArrayName: 'phone_numbers'
        }
      ]
    };
  },
  computed: {
    localTitle() {
      if (this.title) {
        return this.title;
      }

      return 'Addresses';
    },
    localSubtitle() {
      if (this.subtitle) {
        return this.subtitle;
      }

      return "Please add the Entity's primary address. Once created, secondary addresses may be added.";
    },
    countryOptions() {
      const countryList = [];

      for (const [key, country] of Object.entries(CountryList.countries)) {
        countryList.push(country.name);
      }

      return countryList;
    },
    countryCodeOptions() {
      const countryList = [];

      for (const [key, country] of Object.entries(CountryList.countries)) {
        countryList.push({
          id: parseInt('00' + country.phone),
          name: country.name
        });
      }

      return countryList;
    }
  },
  watch: {
    'value.phone_numbers'(phoneNumbers) {
      if (phoneNumbers.length) {
        phoneNumbers.forEach((phoneDetails) => {
          if (phoneDetails && !phoneDetails.IsPrimaryNumber) {
            phoneDetails.IsPrimaryNumber = 0;
          }
        })
      }
    },
  },
  methods: {
    async postOptions() {
      await this.loadModelReferenceIntoObject(
        'CountryCode',
        'countryCodeOptions',
        'phone_numbers'
      );
    },
    selectAddress(address) {
      address.AddressType_FK = null;
      address.phone_numbers = [];

      this.model = address;
    }
  }
};
</script>

<style scoped>
</style>
